import React, { useState, useEffect } from "react";
import { Row, Col, Card, Collapse, Radio, Space, message } from "antd";
import { Line, Bar } from 'react-chartjs-2';
import { Chart as ChartJS, registerables } from 'chart.js';
import axios from "axios";
import moment from 'moment'

import AnalysisFilters from '../Analysis/AnalysisFilters'
import { CALENDAR_MONTHS } from "../../constants/dates";

ChartJS.register(...registerables);

const endOfThisMonth = moment().endOf('month')
const oneYearAgo = moment().subtract(12, 'months').startOf('month')

const UserAnalytic = ({ merchantId }) => {
  const [filters, setFilters] = useState({ startDate: oneYearAgo, endDate: endOfThisMonth })
  const [dimension, setDimension] = useState({ time: "month" })
  const [monthlyOrderStats, setMonthlyOrderStats] = useState([])
  const [orderStats, setOrderStats] = useState([])
  const [weeklyOrderStats, setWeeklyOrderStats] = useState([])

  useEffect(() => {
    const dateParams = {
      startDate: filters.startDate?.startOf('month')?.toISOString(),
      endDate: filters.endDate?.endOf('month')?.toISOString(),
    }

    const fetchMonthlyOrderStats = async () => {
      try {
        const res = await axios.get(`/api/analysis/monthly-order`, {
          params: { 
            merchantId,
            ...dateParams }
        })
        setMonthlyOrderStats(res.data.data)
      } catch (error) {
        message.error(error.response?.data?.message || error.message)
      }
    }

    const fetchOrderStats = async () => {
      try {
        const res = await axios.get(`/api/analysis/order-stats`, {
          params: { 
            merchantId,
            ...dateParams }
        })
        setOrderStats(res.data.data ?? [])

        const rawData = res.data.data ?? []

        const flatWeeks = rawData.flatMap(item =>
          (item.weeks ?? []).map(week => ({
            year: item.year,
            month: item.month,
            week: week.week,
            weeklyOrderCount: week.weeklyOrderCount,
          }))
        )

        setWeeklyOrderStats(flatWeeks)
      } catch (error) {
        message.error(error.response?.data?.message || error.message)
      }
    }

    fetchMonthlyOrderStats()
    fetchOrderStats()
  }, [filters.startDate, filters.endDate, merchantId])

  return (
    <React.Fragment>
      <Collapse>
        <Collapse.Panel header={`Filters (${filters.startDate.format('MMM YYYY')} - ${filters.endDate.format('MMM YYYY')})`} key="filters">
          <Space wrap>
            <AnalysisFilters
              filters={filters} 
              onFiltersChange={filters => setFilters(filters)} 
            />
            Time Dimension :
            <Radio.Group
              options={[{ label: "Week", value: "week" }, { label: "Month", value: "month" }]}
              value={dimension.time}
              onChange={(e) => setDimension({ ...dimension, time: e.target.value })}
              optionType="button"
              buttonStyle="solid"
            />
          </Space>
        </Collapse.Panel>
      </Collapse>

      <Row gutter={16} className="mt-4">
        <Col md={12}>
          <Card title="Order RTO Rate (%)">
          <Line
            data={{
              labels: monthlyOrderStats.map(stat => `${stat.year}-${stat.month}`),
              datasets: [{
                label: 'RTO (%)',
                backgroundColor: 'rgb(99, 99, 132)',
                fill: true,
                order: 1,
                borderColor: 'rgb(99, 99, 132)',
                data: monthlyOrderStats?.map(stat => {
                  return ((stat.returnedCount / (stat.totalCount || 0)) * 100).toFixed(2)
                }),
              }]
            }}
            options={{
              interaction: {
                mode: 'index'
              },
              plugins: {
                legend: {
                  display: false
                },
                tooltip: {
                  callbacks: {
                    label: function(context) {
                      const totalCount = monthlyOrderStats[context.dataIndex].totalCount
                      const returnedCount = monthlyOrderStats[context.dataIndex].returnedCount

                      return `${context.formattedValue}% : ${returnedCount} return requests in ${totalCount} Order`
                    }
                  }
                }
              }
            }}
          />
          </Card>
        </Col>
        <Col md={12}>
          <Card title="Order Count">
            <Bar
              data={{
                labels: dimension.time === "month"
                  ? orderStats.map(stat => `${stat.year}-${stat.month}`)
                  : weeklyOrderStats.map(stat => {
                      const monthName = CALENDAR_MONTHS[stat.month - 1]?.name;
                      return stat.week === 1
                        ? [`W${stat.week}`, monthName?.substring(0, 3), stat.year]
                        : [`W${stat.week}`];
                    }),
                datasets: [{
                  label: 'Order Count',
                  backgroundColor: "rgba(0, 200, 83, 0.6)",
                  borderColor: "rgb(0, 200, 83)",
                  borderWidth: 1,
                  data: dimension.time === "month"
                    ? orderStats.map(stat => stat.monthlyOrderCount ?? 0)
                    : weeklyOrderStats.map(stat => stat.weeklyOrderCount ?? 0)
                }]
              }}
              options={{
                plugins: {
                  legend: {
                    display: false
                  }
                }
              }}
            />
          </Card>
        </Col>
      </Row>
    </React.Fragment>
  )
}

export default UserAnalytic;
